"use client";

import { signedPct } from "@/lib/format/change";
import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useT, useLocale } from "@/lib/locale-context";
import { useTheme } from "@/lib/theme-context";
import { fetchAPI } from "@/lib/api";
import { formatMonthDayShort, formatDateLong } from "@/lib/blog-format";
import { SEGMENT_OFF, SEGMENT_ON } from "@/lib/ui-classes";

const Chart = dynamic(() => import("react-apexcharts"), { ssr: false });

interface TrendData {
  week: string;
  median_price: number | null;
  median_ppm: number | null;
  listing_count: number;
  new_listings: number | null;
  removed_listings: number | null;
  price_drops: number | null;
}

interface Props {
  level: string;          // "district" | "hex8" | "complex"
  locationKey: string;    // district name, hex id, complex name
  mode: "sale" | "rent";
  rooms?: number;
  weeks?: number;
  /** Optional content rendered inside TrendChart's own card, above
   *  the existing metric chrome. TrendPanel uses this to render its
   *  header (icon + title) and level tabs INSIDE the chart card so
   *  the header isn't floating on the page background outside any
   *  visible surface. */
  preHeader?: React.ReactNode;  /** Pointer scrub FX (gradient crosshair band + cursor-following
   *  tooltip). Right for the big residence/trends charts; the compact
   *  result-page panel turns it off - the 28px band + roaming tooltip
   *  overlapped the AZN/m² copy and read as a glitch there. */
  scrub?: boolean;
  /** Overlay the Bakı-wide weekly ppm median as a dashed gray line so
   *  "is this district outperforming" reads in one glance. District
   *  level + ppm metric only (the baseline series has no price). */
  baseline?: boolean;
}

// Compact price for the y-axis. Sale prices are hundreds of thousands
// (250000 → "250k"), but rent prices live in the hundreds-to-low-
// thousands where dividing by 1000 and rounding destroys the number
// (1100 → "1k", which reads as 1,000 - the bug this fixes). Pick the
// unit from the magnitude so both sale and rent axes stay legible.
function formatPriceCompact(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (v >= 10_000) return `${Math.round(v / 1000)}k`;
  if (v >= 1_000) return `${(v / 1000).toFixed(1)}k`;
  return `${Math.round(v)}`;
}

// Headline price value. Sale-scale stays compact ("250k"); rent-scale
// shows the real grouped number ("1,100") rather than a misleading "1k".
function formatPriceHeader(v: number): string {
  if (v >= 10_000) return `${Math.round(v / 1000)}k`;
  return Math.round(v).toLocaleString();
}

// "2026-W15" → Date pointing at that ISO-week's Monday (UTC)
function weekToDate(weekKey: string): number {
  const m = /^(\d{4})-W(\d{2})$/.exec(weekKey);
  if (!m) return Date.now();
  const year = parseInt(m[1], 10);
  const week = parseInt(m[2], 10);
  // ISO week 1 = week with Jan 4th. Compute Monday of given ISO week in UTC.
  const jan4 = Date.UTC(year, 0, 4);
  const jan4Dow = new Date(jan4).getUTCDay() || 7; // 1..7, Monday=1
  const isoWeek1Monday = jan4 - (jan4Dow - 1) * 86400000;
  return isoWeek1Monday + (week - 1) * 7 * 86400000;
}

export default function TrendChart({ level, locationKey, mode, rooms = 0, weeks = 52, preHeader, scrub = true, baseline = false }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const { resolved } = useTheme();
  const isDark = resolved === "dark";
  const [data, setData] = useState<TrendData[]>([]);
  const [baselineData, setBaselineData] = useState<Array<{ week: string; median_ppm: number }>>([]);
  const [loading, setLoading] = useState(true);
  // Default metric is mode-dependent: sale viewers care about
  // AZN/m² (it normalises for apartment size, which varies widely),
  // but rent viewers care about the monthly price itself - "what
  // am I going to pay" is more immediately useful than rent-per-m².
  // They can still toggle to the other metric via the segmented
  // control in the header.
  const [metric, setMetric] = useState<"price" | "ppm">(mode === "rent" ? "price" : "ppm");

  // After the first paint the chart stays on screen while another
  // level or metric loads, dimmed; swapping it for the skeleton made the
  // card jump (owner, 2026-10-11: "the charts jump when I change the
  // market trend").
  const [everLoaded, setEverLoaded] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchAPI("/trends", {
      level,
      key: locationKey,
      mode,
      rooms: String(rooms),
      weeks: String(weeks),
      ...(baseline ? { baseline: "1" } : {}),
    })
      .then((res: unknown) => {
        const r = res as { data?: TrendData[]; baseline?: Array<{ week: string; median_ppm: number }> };
        setData(r?.data || []);
        setBaselineData(r?.baseline || []);
      })
      .catch(() => {
        setData([]);
        setBaselineData([]);
      })
      .finally(() => { setLoading(false); setEverLoaded(true); });
  }, [level, locationKey, mode, rooms, weeks, baseline]);

  if (loading && !everLoaded) {
    // Skeleton must match the final mounted component's layout box
    // exactly, otherwise the header row + chart heights shift when
    // data arrives and every trend slug ships CLS >= 0.19 per
    // TrendChart. Reserve: header row (eyebrow label + value span +
    // metric-tab toggle, ~38 px incl margin) + the chart box
    // (`height={200}` on the real <Chart>). mb-3 + mt-0.5 combined
    // match the final spacing.
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        {/* Keep the preHeader (TrendPanel's level tabs) mounted while
            loading - dropping it here made the tabs vanish for the
            duration of every level switch. */}
        {preHeader && (
          <div className="mb-4 pb-4 border-b border-gray-100 space-y-3">
            {preHeader}
          </div>
        )}
        <div className="flex items-center justify-between mb-3">
          <div className="space-y-1.5">
            <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
            <div className="h-5 w-28 bg-gray-100 rounded animate-pulse" />
          </div>
          <div className="h-7 w-[100px] bg-gray-100 rounded-lg animate-pulse" />
        </div>
        <div className="h-[200px] bg-gray-50 rounded-xl animate-pulse" />
      </div>
    );
  }

  // Keep only weeks with a real value - otherwise categories/values misalign
  // and ApexCharts renders missing points as phantom zeros.
  const points = data
    .map((d) => {
      const v = metric === "ppm" ? d.median_ppm : d.median_price;
      return v && v > 0 ? { x: weekToDate(d.week), y: v } : null;
    })
    .filter((p): p is { x: number; y: number } => p !== null);

  if (points.length === 0) {
    // The empty state MUST still render the preHeader slot: that's
    // where TrendPanel's level tabs live. Omitting it locked users
    // out of the chart entirely - switch to a level with no data
    // (e.g. a residence with no aggregates yet) and the tabs to
    // switch back disappeared with the chart.
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        {preHeader && (
          <div className="mb-4 pb-4 border-b border-gray-100 space-y-3">
            {preHeader}
          </div>
        )}
        <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
          </svg>
        </div>
        <p className="text-sm text-gray-500 font-medium">{t("trends.nodata")}</p>
        <p className="text-xs text-gray-400 mt-1">{t("trends.nodata.sub")}</p>
        </div>
      </div>
    );
  }

  const first = points[0].y;
  const last = points[points.length - 1].y;
  const changePct = first && last ? ((last - first) / first) * 100 : 0;
  const isUp = changePct > 1;
  const isDown = changePct < -1;
  const lineColor = isDown ? "#059669" : isUp ? "#e11d48" : "#6b7280";

  // Bakı baseline overlay - ppm metric only (the baseline carries no
  // price series). Needs 2+ points to draw a meaningful line.
  const baselinePoints =
    baseline && metric === "ppm"
      ? baselineData
          .map((d) => (d.median_ppm > 0 ? { x: weekToDate(d.week), y: d.median_ppm } : null))
          .filter((p): p is { x: number; y: number } => p !== null)
      : [];
  const showBaseline = baselinePoints.length >= 2;
  const baselineColor = isDark ? "#6b7280" : "#9ca3af";

  // Grid hairlines + axis labels. In dark mode the earlier values
  // (gray-800 grid, gray-500 labels) read as near-invisible on the
  // gray-50-that-swaps-to-dark page bg; bump labels one step into
  // the visible range and lift the grid so the chart has any
  // readable scaffolding at all.
  const gridColor = isDark ? "#374151" : "#f3f4f6";
  const labelColor = isDark ? "#9ca3af" : "#9ca3af";

  // With only a handful of weekly points (common at the residence/complex
  // level, where aggregates are still filling in) a "smooth" spline
  // invents an S-curve between points - the chart reads like a dramatic
  // surge when it's really two sparse samples. Draw straight segments and
  // show the actual markers so the data isn't editorialised.
  const sparse = points.length <= 6;

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: "area",
      height: 200,
      sparkline: { enabled: false },
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "system-ui, -apple-system, sans-serif",
      background: "transparent",
      // Soft glow under the stroke - the line reads as lit, not drawn
      // (Revolut-style tactile chart; the scrub interactions below
      // complete it).
      dropShadow: {
        enabled: true,
        enabledOnSeries: [0],
        color: lineColor,
        blur: 8,
        opacity: isDark ? 0.45 : 0.3,
        top: 0,
        left: 0,
      },
    },
    theme: { mode: isDark ? "dark" : "light" },
    colors: showBaseline ? [lineColor, baselineColor] : [lineColor],
    stroke: {
      curve: sparse ? "straight" : "smooth",
      width: showBaseline ? [2.5, 1.8] : 2.5,
      colors: showBaseline ? [lineColor, baselineColor] : [lineColor],
      dashArray: showBaseline ? [0, 5] : 0,
    },
    fill: showBaseline
      ? {
          // Per-series fill: gradient under the district line only,
          // the dashed baseline stays a bare line (opacity 0).
          type: ["gradient", "solid"],
          opacity: [1, 0],
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.15,
            opacityTo: 0.02,
            stops: [0, 100],
          },
        }
      : {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.15,
            opacityTo: 0.02,
            stops: [0, 100],
            colorStops: [
              { offset: 0, color: lineColor, opacity: 0.15 },
              { offset: 100, color: lineColor, opacity: 0.02 },
            ],
          },
        },
    legend: {
      show: showBaseline,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "11px",
      labels: { colors: labelColor },
      itemMargin: { horizontal: 10 },
    },
    xaxis: {
      type: "datetime",
      // Scrub feedback: a soft gradient band tracks the pointer along
      // the x-axis instead of a hard hairline - dragging across the
      // chart feels continuous rather than dot-to-dot.
      crosshairs: scrub
        ? {
            show: true,
            width: 28,
            opacity: 1,
            fill: {
              type: "gradient",
              gradient: {
                colorFrom: lineColor,
                colorTo: lineColor,
                opacityFrom: 0.0,
                opacityTo: isDark ? 0.22 : 0.14,
              },
            },
            stroke: { width: 0 },
          }
        : { show: false },
      labels: {
        style: { fontSize: "10px", colors: labelColor },
        datetimeUTC: true,
        // Day-precision so weekly data points always render distinct
        // labels. The previous "MMM yy" format collapsed every tick
        // inside a single month to "Feb 26", which - with tickAmount
        // 6 ticks evenly spread over an 8-week span - produced three
        // identical "Feb 26" labels in a row. Year is dropped because
        // the chart's range selector + section header carry it; the
        // axis just needs a unique anchor per tick. Localized via a
        // formatter - ApexCharts' built-in "d MMM" is English-only.
        formatter: (value: string) => formatMonthDayShort(Number(value), locale),
        hideOverlappingLabels: true,
      },
      tickAmount: 6,
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { fontSize: "10px", colors: labelColor },
        formatter: (v: number) => metric === "ppm" ? `${Math.round(v)}` : formatPriceCompact(v),
      },
    },
    grid: { borderColor: gridColor, strokeDashArray: 4 },
    tooltip: {
      theme: isDark ? "dark" : "light",
      followCursor: scrub,
      shared: showBaseline,
      intersect: false,
      x: { formatter: (value: number) => formatDateLong(Number(value), locale) },
      y: {
        formatter: (v: number) => metric === "ppm"
          ? `${Math.round(v)} AZN/m²`
          : `${Math.round(v).toLocaleString()} AZN`,
      },
    },
    dataLabels: { enabled: false },
    // The active point wears a translucent halo (fat low-alpha stroke
    // ring) so hover reads as a glow blooming under the finger, not a
    // bare dot appearing.
    markers: {
      size: showBaseline ? [sparse ? 4 : 0, 0] : sparse ? 4 : 0,
      colors: [lineColor],
      strokeWidth: 8,
      strokeColors: `${lineColor}2e`,
      hover: { size: 7, sizeOffset: 3 },
    },
  };

  const series = showBaseline
    ? [
        { name: locationKey, data: points },
        { name: t("trends.baseline.baku"), data: baselinePoints },
      ]
    : [{
        name: metric === "ppm" ? t("trends.ppm") : t("trends.price"),
        data: points,
      }];

  return (
    <div className={`rounded-2xl border border-gray-200 bg-white p-5 transition-opacity duration-200 ${loading ? "opacity-60" : ""}`}>
      {/* Optional header slot - TrendPanel uses this to render its
          title + level tabs INSIDE this card instead of letting them
          float on the page bg above. Separated from the existing
          metric chrome by a hairline so the two concerns read as
          distinct: "what trend / where / at which level" above the
          rule, "how much / sale vs rent / ppm vs price" below it. */}
      {preHeader && (
        <div className="mb-4 pb-4 border-b border-gray-100 space-y-3">
          {preHeader}
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs tracking-wide text-gray-500 font-semibold">
            {mode === "rent" ? t("trends.rent") : t("trends.sale")}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-lg font-bold text-gray-900">
              {metric === "ppm"
                ? `${Math.round(last)} AZN/m²`
                : `${formatPriceHeader(last)} AZN`}
            </span>
            {changePct !== 0 && (
              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                isDown ? "bg-emerald-50 text-emerald-700" : isUp ? "bg-rose-50 text-rose-700" : "bg-gray-50 text-gray-600"
              }`}>
                {signedPct(changePct, 1)}
              </span>
            )}
          </div>
        </div>
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setMetric("ppm")}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              metric === "ppm" ? SEGMENT_ON : SEGMENT_OFF
            }`}
          >
            AZN/m²
          </button>
          <button
            onClick={() => setMetric("price")}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              metric === "price" ? SEGMENT_ON : SEGMENT_OFF
            }`}
          >
            {t("trends.price")}
          </button>
        </div>
      </div>
      {/* Fewer than 3 weeks draw a dot or a lone segment, not a trend
          (owner, 2026-11-02): the numbers above stay, the chart goes. */}
      {points.length >= 3 && <Chart options={options} series={series} type="area" height={200} />}
    </div>
  );
}
